import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useState } from 'react';

const ENDPOINT_URL = '/srv-services/my-account/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type DeleteServiceInput = paths[url]['delete']['parameters']['path'];

/**
 * Requests endpoint to (soft) delete a user's services.
 */
export const useDeleteService = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async (props: DeleteServiceInput) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{uuid}', props.uuid || '')}`, {
                method: 'DELETE',
                headers: getHeaders(),
                body: JSON.stringify(getCommonParameters()),
            });

            if (!response.ok) {
                let error;

                try {
                    const json = await response.json();
                    error = new Error(json.message || '') as ApiError;
                    error.statusCode = response.status;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    // eslint-disable-next-line no-ex-assign
                    error = new Error() as ApiError;
                    error.statusCode = response.status;
                }

                throw error;
            }

            try {
                const body = await response.json();
                const message =
                    typeof (body as { message?: string })?.message === 'string'
                        ? (body as { message?: string }).message
                        : typeof (body as { data?: { message?: string } })?.data?.message === 'string'
                          ? (body as { data?: { message?: string } }).data?.message ?? null
                          : null;
                setSuccess(message ?? null);
            } catch (parseError) {
                setSuccess(null);
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setError(error.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, success, lastResponseTime };
};
